import { AnalyticsEvent, PerformanceMetrics } from '../types/enhancedAnalysis';

interface AnalyticsConfig {
  trackingId?: string;
  apiEndpoint?: string;
  enableDebug?: boolean;
  batchSize?: number;
  flushInterval?: number;
}

class AnalyticsService {
  private config: AnalyticsConfig;
  private eventQueue: AnalyticsEvent[] = [];
  private performanceQueue: PerformanceMetrics[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;

  constructor(config: AnalyticsConfig = {}) {
    this.config = {
      batchSize: 10,
      flushInterval: 30000, // 30秒
      enableDebug: import.meta.env.DEV,
      ...config
    };
  }

  public initialize() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.startFlushTimer();
    this.initializePerformanceTracking();
    
    if (this.config.enableDebug) {
      console.log('Analytics service initialized', this.config);
    }
  }

  private startFlushTimer() {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  private initializePerformanceTracking() {
    // 监听页面性能指标
    if ('PerformanceObserver' in window) {
      // Core Web Vitals
      this.observeWebVitals();
      
      // 自定义性能指标
      this.trackCustomPerformanceMetrics();
    }
  }

  private observeWebVitals() {
    try {
      // LCP (Largest Contentful Paint)
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.trackPerformanceMetric('LCP', lastEntry.startTime);
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // FID (First Input Delay)
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry: any) => {
          this.trackPerformanceMetric('FID', entry.processingStart - entry.startTime);
        });
      }).observe({ entryTypes: ['first-input'] });

      // CLS (Cumulative Layout Shift)
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        let clsValue = 0;
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        this.trackPerformanceMetric('CLS', clsValue);
      }).observe({ entryTypes: ['layout-shift'] });

    } catch (error) {
      console.warn('Performance observation not supported:', error);
    }
  }

  private trackCustomPerformanceMetrics() {
    // 页面加载完成后收集指标
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const paint = performance.getEntriesByType('paint');
        
        const metrics: Partial<PerformanceMetrics> = {
          timeToInteractive: this.calculateTTI(),
          contentLoadTime: navigation.loadEventEnd - navigation.loadEventStart,
          bundleSize: this.estimateBundleSize(),
          networkRequests: performance.getEntriesByType('resource').length,
          cacheHitRate: this.calculateCacheHitRate(),
          errorRate: 0 // 需要从错误追踪中获取
        };

        // 获取 FCP (First Contentful Paint)
        const fcp = paint.find(entry => entry.name === 'first-contentful-paint');
        if (fcp) {
          metrics.contentLoadTime = fcp.startTime;
        }

        this.trackPerformanceMetrics(metrics);
      }, 1000);
    });
  }

  private calculateTTI(): number {
    // 简化的 TTI 计算
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    return navigation.loadEventEnd - navigation.fetchStart;
  }

  private estimateBundleSize(): number {
    // 估算主要资源大小
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    return resources
      .filter(resource => resource.name.includes('.js') || resource.name.includes('.css'))
      .reduce((total, resource) => total + (resource.transferSize || 0), 0);
  }

  private calculateCacheHitRate(): number {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const cachedResources = resources.filter(resource => resource.transferSize === 0);
    return resources.length > 0 ? cachedResources.length / resources.length : 0;
  }

  private trackPerformanceMetric(name: string, value: number) {
    const metric = {
      [name]: value,
      timestamp: Date.now()
    } as any;

    this.performanceQueue.push(metric);
    
    if (this.config.enableDebug) {
      console.log(`Performance metric: ${name} = ${value}`);
    }
  }

  private trackPerformanceMetrics(metrics: Partial<PerformanceMetrics>) {
    this.performanceQueue.push(metrics as PerformanceMetrics);
    
    if (this.config.enableDebug) {
      console.log('Performance metrics:', metrics);
    }
  }

  // 公共方法
  public track(event: Omit<AnalyticsEvent, 'timestamp'>) {
    const fullEvent: AnalyticsEvent = {
      ...event,
      timestamp: new Date()
    };

    this.eventQueue.push(fullEvent);

    if (this.config.enableDebug) {
      console.log('Analytics event:', fullEvent);
    }

    // 如果队列达到批量大小，立即发送
    if (this.eventQueue.length >= (this.config.batchSize || 10)) {
      this.flush();
    }
  }

  public trackPageView(page: string, title?: string) {
    this.track({
      category: 'engagement',
      action: 'page_view',
      label: page,
      customDimensions: {
        page_title: title || document.title,
        referrer: document.referrer,
        user_agent: navigator.userAgent
      },
      sessionId: this.generateSessionId(),
      analysisId: this.extractAnalysisId()
    });
  }

  public trackUserAction(action: string, category: 'engagement' | 'conversion' | 'content' | 'social' | 'performance' | 'error' = 'engagement', label?: string, value?: number) {
    this.track({
      category,
      action,
      label,
      value,
      sessionId: this.generateSessionId(),
      analysisId: this.extractAnalysisId()
    });
  }

  public trackConversion(type: string, value: number, metadata?: Record<string, any>) {
    this.track({
      category: 'conversion',
      action: type,
      value,
      customDimensions: metadata,
      sessionId: this.generateSessionId(),
      analysisId: this.extractAnalysisId()
    });
  }

  public trackError(error: Error, context?: string) {
    this.track({
      category: 'error',
      action: 'javascript_error',
      label: error.message,
      customDimensions: {
        error_name: error.name,
        error_stack: error.stack || '',
        context: context || '',
        user_agent: navigator.userAgent,
        url: window.location.href
      },
      sessionId: this.generateSessionId(),
      analysisId: this.extractAnalysisId()
    });
  }

  public trackTiming(category: string, variable: string, time: number, label?: string) {
    this.track({
      category: 'performance',
      action: 'timing',
      label: `${category}_${variable}`,
      value: time,
      customDimensions: {
        timing_category: category,
        timing_variable: variable,
        timing_label: label || ''
      },
      sessionId: this.generateSessionId(),
      analysisId: this.extractAnalysisId()
    });
  }

  public async flush() {
    if (this.eventQueue.length === 0 && this.performanceQueue.length === 0) {
      return;
    }

    const payload = {
      events: [...this.eventQueue],
      performance: [...this.performanceQueue],
      timestamp: new Date().toISOString(),
      session: {
        id: this.generateSessionId(),
        url: window.location.href,
        referrer: document.referrer,
        userAgent: navigator.userAgent
      }
    };

    // 清空队列
    this.eventQueue = [];
    this.performanceQueue = [];

    try {
      await this.sendToEndpoint(payload);
      
      if (this.config.enableDebug) {
        console.log('Analytics data sent successfully');
      }
    } catch (error) {
      console.error('Failed to send analytics data:', error);
      
      // 如果发送失败，可以选择重新加入队列或丢弃
      // 这里选择丢弃以避免内存泄漏
    }
  }

  private async sendToEndpoint(payload: any) {
    if (this.config.apiEndpoint) {
      // 发送到自定义API端点
      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    }

    // 发送到 Google Analytics (如果配置了)
    if (this.config.trackingId && typeof (window as any).gtag !== 'undefined') {
      payload.events.forEach((event: AnalyticsEvent) => {
        (window as any).gtag('event', event.action, {
          event_category: event.category,
          event_label: event.label,
          value: event.value,
          custom_map: event.customDimensions
        });
      });
    }

    // 发送到其他分析服务 (Mixpanel, Amplitude 等)
    this.sendToThirdPartyServices(payload);
  }

  private sendToThirdPartyServices(payload: any) {
    // Mixpanel
    if (typeof (window as any).mixpanel !== 'undefined') {
      payload.events.forEach((event: AnalyticsEvent) => {
        (window as any).mixpanel.track(event.action, {
          category: event.category,
          label: event.label,
          value: event.value,
          ...event.customDimensions
        });
      });
    }

    // Amplitude
    if (typeof (window as any).amplitude !== 'undefined') {
      payload.events.forEach((event: AnalyticsEvent) => {
        (window as any).amplitude.getInstance().logEvent(event.action, {
          category: event.category,
          label: event.label,
          value: event.value,
          ...event.customDimensions
        });
      });
    }
  }

  private generateSessionId(): string {
    // 尝试从 sessionStorage 获取，如果没有则生成新的
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  private extractAnalysisId(): string {
    // 从 URL 中提取分析ID
    const pathMatch = window.location.pathname.match(/\/analysis\/([^\/]+)/);
    return pathMatch ? pathMatch[1] : '';
  }

  // A/B测试支持
  public trackExperiment(experimentId: string, variantId: string) {
    this.track({
      category: 'engagement',
      action: 'variant_assigned',
      label: experimentId,
      customDimensions: {
        experiment_id: experimentId,
        variant_id: variantId
      },
      sessionId: this.generateSessionId(),
      analysisId: this.extractAnalysisId()
    });
  }

  public trackExperimentGoal(experimentId: string, goalName: string, value?: number) {
    this.track({
      category: 'conversion',
      action: 'goal_achieved',
      label: goalName,
      value,
      customDimensions: {
        experiment_id: experimentId,
        goal_name: goalName
      },
      sessionId: this.generateSessionId(),
      analysisId: this.extractAnalysisId()
    });
  }

  // 清理资源
  public destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // 最后发送剩余数据
    this.flush();
  }
}

// 创建全局实例
export const analyticsService = new AnalyticsService({
  apiEndpoint: import.meta.env.VITE_ANALYTICS_ENDPOINT,
  trackingId: import.meta.env.VITE_GA_TRACKING_ID,
  enableDebug: import.meta.env.DEV
});

// 自动初始化
if (typeof window !== 'undefined') {
  analyticsService.initialize();
}

export { AnalyticsService };
export default analyticsService;